import {
  initializeFaro,
  getWebInstrumentations,
  type Faro,
} from '@grafana/faro-web-sdk'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'

let faroInstance: Faro | null = null

interface FaroBootOptions {
  tenant: string
  appName: string
  appVersion: string
  environment: string
}

/**
 * Faro Web SDK'yı başlatır. Çevre değişkenleri:
 *  - VITE_FARO_URL: ingest endpoint (Alloy faro receiver). Boş ise no-op.
 *  - VITE_FARO_DISABLED: "true" -> tamamen kapalı.
 *
 * Tenant scope için, login akışı sırasında setUser/setSession çağırılır.
 */
export function initFaro(opts: FaroBootOptions): Faro | null {
  if (faroInstance) return faroInstance

  const url = import.meta.env.VITE_FARO_URL
  const disabled = import.meta.env.VITE_FARO_DISABLED === 'true'

  if (!url || disabled) {
    return null
  }

  // Window-level kill switch (tenant gate). API'den dönen
  // monitoring_disabled flag'i gerekirse buraya set edilir.
  if ((window as unknown as { __monitoring_disabled?: boolean }).__monitoring_disabled) {
    return null
  }

  faroInstance = initializeFaro({
    url,
    app: {
      name: opts.appName,
      version: opts.appVersion,
      environment: opts.environment,
    },
    instrumentations: [
      ...getWebInstrumentations({
        captureConsole: false, // gürültülü, sadece error capture default
      }),
      new TracingInstrumentation({
        instrumentationOptions: {
          // Backend'imize giden tüm istekler için W3C traceparent inject.
          propagateTraceHeaderCorsUrls: [
            /^https?:\/\/.*\.yarisma\.online.*/,
            /^https?:\/\/localhost:300\d.*/,
          ],
        },
      }),
    ],
    sessionTracking: {
      enabled: true,
      samplingRate: 1.0,
    },
    beforeSend: (event) => {
      // PII scrub: token query parametreleri redact edilir.
      try {
        const ev = event as { meta?: { page?: { url?: string } } }
        if (ev.meta?.page?.url) {
          ev.meta.page.url = ev.meta.page.url.replace(
            /([?&](?:token|access_token|refresh_token)=)[^&]+/gi,
            '$1[REDACTED]',
          )
        }
      } catch {
        // ignore
      }
      return event
    },
  })

  // Tenant code attribute olarak işaretle.
  faroInstance.api.setSession({
    id: cryptoRandomId(),
    attributes: { tenant: opts.tenant },
  })

  return faroInstance
}

/**
 * Login sonrası yarismaci_id ile kullanıcıyı eşleştirir.
 */
export function setFaroUser(yarismaciId: string, sessionId?: string): void {
  if (!faroInstance) return
  faroInstance.api.setUser({
    id: yarismaciId,
  })
  if (sessionId) {
    faroInstance.api.setSession({
      id: sessionId,
      attributes: { yarismaci_id: yarismaciId },
    })
  }
}

/**
 * Logout / oturum sonu — Faro session'ını yeniler.
 */
export function clearFaroUser(): void {
  if (!faroInstance) return
  faroInstance.api.resetUser()
}

/**
 * Custom span başlat — örneğin "yarismaci.cevap.submit" gibi.
 * Faro tracing instrumentation üzerinden span oluşturulur.
 */
export function startFaroSpan(
  name: string,
  attributes: Record<string, string | number | boolean> = {},
): { end: () => void } | null {
  if (!faroInstance) return null
  const otel = faroInstance.api.getOTEL?.()
  if (!otel) return null
  const tracer = otel.trace.getTracer('yarisma-online2-app')
  const span = tracer.startSpan(name, { attributes })
  return {
    end: () => span.end(),
  }
}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}
